import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ArrowLeft, CalendarPlus, Check, ChevronRight, Clock, ImagePlus, MessageSquare, Phone, Plus, Search, Send, Sparkles, X } from 'lucide-react';
import { api, API_BASE_URL, SERVER_ORIGIN } from '../services/api';
import { useReadOnly } from '../hooks/useReadOnly';
import { BUSINESS_TZ, fmtTs, parseDbTs } from '../utils/time';
import { shrinkPhoto } from '../utils/image';
import AccountMark from '../components/AccountMark';

/**
 * Texts — every conversation on the business number, in the app (owner, Oct 14 2026: "I want the app to fully
 * do all messaging through it using the Twilio thing. So I can send through the app and receive through the
 * app, and then soon all customers text through the app.")
 *
 * One thread per phone number (server/services/texts): what the app sent them (bills, on-my-way, bids — each
 * with a small chip saying which), what they texted back, what they typed in the request box on their own
 * customer page (a dashed bubble, "From their customer page" — owner, Oct 20 2026: "I can't see a client's
 * message but I got it on my number"), and the replies typed here, which go out from the business number. Opening a thread clears its unread count; Needs Attention keeps listing a customer until
 * a reply is typed. A picture they text in shows in the thread; one picture can ride on a reply (Add a photo —
 * owner, Oct 20 2026: "allow photo upload in app texts"), shrunk on the phone first, 5 MB at most after that.
 */
// The biggest picture a text may carry once shrunk (server/routes/messages: the same number).
const PHOTO_MAX_BYTES = 5 * 1024 * 1024;
type Kind = 'customer' | 'lead' | 'crew' | 'unknown';
type Thread = {
  phone: string; pretty: string; name: string | null; label?: string | null; kind: Kind; who_id: number | null; customer_id: number | null; opted_out: boolean;
  account_mark?: 'P' | 'C' | null;
  total: number; unread: number; last_at: string; last_dir: 'in' | 'out'; last_body: string; last_words: string | null;
};
type Media = { url: string; type: string | null; ours?: boolean };
type Msg = {
  id: number; dir: 'in' | 'out'; body: string; at: string; type: string | null; words: string | null; typed: boolean; by: string | null;
  status: string | null; failed: boolean; error: string | null; read_at: string | null; media: Media[]; media_failed?: string | null;
  job_id: number | null; invoice_id: number | null; quote_id: number | null;
  // Typed on their customer page, not texted (server/services/texts PORTAL_TYPE): shown as an incoming bubble that
  // says so; request_id is the Needs Attention item ("<name> asked for …") it opens.
  portal?: boolean; request_id?: number | null;
};
/** A text typed in the quiet hours, waiting for the morning (server/services/heldTexts). `failed` = the morning refused it. */
type Held = {
  id: number; kind: 'reply' | 'custom'; phone: string; body: string; media: Media | null; send_day: string; send_hm: string; words: string;
  created_at: string; by: string | null; error: string | null; tries: number; failed: boolean; job_id: number | null; quote_id: number | null; type: string;
};
/** The quiet hours right now: is it after 8 PM, and when would a text typed now go. */
type Quiet = { on: boolean; from: string; until: string; from_words: string; until_words: string; active: boolean; send_day: string | null; send_hm: string | null; words: string | null };
type Conversation = {
  phone: string; pretty: string; to: string; name: string | null; label?: string | null; kind: Kind; who_id: number | null; customer_id: number | null;
  account_mark?: 'P' | 'C' | null; opted_out: boolean; unread: number; messages: Msg[];
  quiet?: Quiet; held?: Held[];
};
type HeldLite = { id: number; phone: string; body: string; send_day: string; send_hm: string; words: string; failed: boolean };
/** What a text asked for (server/services/textAsks): open until the app's own data says it was finished. */
type AskKind = 'quote' | 'reschedule' | 'question' | 'cancel' | 'pause' | 'complaint' | 'bid_yes' | 'other';
type Ask = {
  id: number; phone: string; who: string | null; kind: AskKind; summary: string; status: 'open' | 'done'; asked_at: string; message_id: number;
  customer_id: number | null; lead_id: number | null; done_how: string | null; note: string | null; missing: string; line: string; action: string;
  facts: { replied: boolean; visit_booked: boolean; visit_moved: boolean; visit_skipped: boolean; schedule_off: boolean; bid_accepted: boolean } | null;
  bid?: { id: number; title: string | null; total: number | null } | null;   // a said-yes ask: the open bid their text accepts
};

const digits10 = (p: string) => p.replace(/\D/g, '').slice(-10);
const dayOf = (ts: string) => { const d = parseDbTs(ts); return d ? d.toLocaleDateString('en-CA', { timeZone: BUSINESS_TZ }) : ''; };
const todayStr = () => new Date().toLocaleDateString('en-CA', { timeZone: BUSINESS_TZ });
/** The list: a time today, a weekday this week, else the date. */
function whenShort(ts: string): string {
  const day = dayOf(ts);
  if (!day) return '';
  if (day === todayStr()) return fmtTs(ts, { hour: 'numeric', minute: '2-digit' });
  const d = parseDbTs(ts)!;
  const ageDays = (Date.now() - d.getTime()) / 864e5;
  return ageDays < 6 ? fmtTs(ts, { weekday: 'short' }) : fmtTs(ts, { month: 'short', day: 'numeric' });
}
/** The separator between days in a thread. */
function dayLabel(ts: string): string {
  const day = dayOf(ts);
  const today = todayStr();
  if (day === today) return 'Today';
  const y = new Date(); y.setDate(y.getDate() - 1);
  if (day === y.toLocaleDateString('en-CA', { timeZone: BUSINESS_TZ })) return 'Yesterday';
  return fmtTs(ts, { weekday: 'short', month: 'short', day: 'numeric' });
}
const KIND_WORDS: Record<Kind, string | null> = { customer: null, lead: 'Lead', crew: 'Crew', unknown: 'Not in the app' };

export default function Texts() {
  const { phone } = useParams<{ phone?: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const readOnly = useReadOnly();
  const [q, setQ] = useState('');
  const [onlyUnread, setOnlyUnread] = useState(false);
  const [picking, setPicking] = useState(false);

  const { data: list, isLoading } = useQuery<{ threads: Thread[]; unread: number; held?: HeldLite[] }>(
    'texts-threads', () => api.get('/messages/threads').then(r => r.data), { refetchInterval: 15000 });
  // Texts typed in the quiet hours, waiting for the morning (server/services/heldTexts): a clock on their thread's row.
  const heldAll = useMemo(() => list?.held || [], [list]);
  const heldByPhone = useMemo(() => new Set(heldAll.map(h => h.phone)), [heldAll]);
  const { data: convo, isLoading: loadingConvo } = useQuery<Conversation>(
    ['texts-thread', phone], () => api.get(`/messages/threads/${phone}`).then(r => r.data),
    { enabled: !!phone, refetchInterval: 8000, keepPreviousData: true });

  // Seen: the moment a thread with unread texts is on the screen (and again when one lands while it is open).
  useEffect(() => {
    if (!phone || !convo || convo.phone !== phone || convo.unread <= 0) return;
    api.post(`/messages/threads/${phone}/read`).then(() => {
      qc.invalidateQueries('texts-threads');
      qc.invalidateQueries('texts-unread');
      qc.setQueryData<Conversation | undefined>(['texts-thread', phone], c => c && ({ ...c, unread: 0 }));
    }).catch(() => { /* the next poll tries again */ });
  }, [phone, convo, qc]);

  const threads = useMemo(() => {
    const words = q.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const dg = q.replace(/\D/g, '');
    return (list?.threads || []).filter(t => {
      if (onlyUnread && !t.unread) return false;
      if (!words.length) return true;
      const hay = `${t.name || ''} ${t.last_body}`.toLowerCase();
      return words.every(w => hay.includes(w)) || (dg.length >= 3 && t.phone.includes(dg));
    });
  }, [list, q, onlyUnread]);

  const open = (p: string) => navigate(`/texts/${p}`);
  const showList = !phone;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-gray-900">Texts</h1>
          <p className="text-sm text-gray-600 mt-0.5">Everything on the business number. Replies go out from it too.</p>
        </div>
        {!readOnly && (
          <button onClick={() => setPicking(true)} className="btn-primary"><Plus className="h-4 w-4 mr-1" /> New text</button>
        )}
      </div>

      <div className="grid gap-4 lg:grid-cols-[22rem_minmax(0,1fr)] items-start">
        {/* The list: every conversation, newest first. On a phone it steps aside for the open thread. */}
        <div className={`card p-0 overflow-hidden ${showList ? '' : 'hidden lg:block'}`}>
          <div className="p-3 border-b border-gray-100 space-y-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Name, number or words" className="input pl-9 w-full" />
            </div>
            <label className="flex items-center gap-2 text-xs font-semibold text-gray-600">
              <input type="checkbox" checked={onlyUnread} onChange={e => setOnlyUnread(e.target.checked)} className="h-4 w-4 rounded border-gray-300" />
              Unread only{list?.unread ? ` (${list.unread})` : ''}
            </label>
            {heldAll.length > 0 && (
              <p className="text-[11px] font-semibold text-amber-800 flex items-center gap-1">
                <Clock className="h-3 w-3 flex-shrink-0" />
                {heldAll.some(h => h.failed)
                  ? `${heldAll.filter(h => h.failed).length} held text${heldAll.filter(h => h.failed).length === 1 ? '' : 's'} didn't send — open the thread`
                  : `${heldAll.length} text${heldAll.length === 1 ? '' : 's'} waiting until ${heldAll[0].words}`}
              </p>
            )}
          </div>
          <ul className="divide-y divide-gray-100 max-h-[70vh] overflow-y-auto">
            {isLoading && <li className="p-4 text-sm text-gray-500">Loading…</li>}
            {!isLoading && !threads.length && (
              <li className="p-4 text-sm text-gray-500">{list?.threads?.length ? 'Nothing matches.' : 'No texts yet. The first one that reaches the business number shows here.'}</li>
            )}
            {threads.map(t => (
              <li key={t.phone}>
                <button onClick={() => open(t.phone)}
                  className={`w-full text-left px-3 py-2.5 hover:bg-gray-50 ${phone === t.phone ? 'bg-brand-50' : ''}`}>
                  <div className="flex items-baseline justify-between gap-2">
                    <span className={`truncate text-sm ${t.unread ? 'font-black text-gray-900' : 'font-semibold text-gray-800'}`}>
                      {t.name || t.pretty}
                      {t.label && <span className="font-normal text-gray-500"> · {t.label}</span>}
                      <AccountMark mark={t.account_mark} />
                      {KIND_WORDS[t.kind] && <span className="ml-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-500">{KIND_WORDS[t.kind]}</span>}
                    </span>
                    <span className="flex-shrink-0 text-[11px] text-gray-500 flex items-center gap-1">
                      {heldByPhone.has(t.phone) && <span title="A text is waiting for the morning"><Clock className="h-3 w-3 text-amber-600" /></span>}
                      {whenShort(t.last_at)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between gap-2 mt-0.5">
                    <span className={`truncate text-xs ${t.unread ? 'text-gray-900 font-semibold' : 'text-gray-500'}`}>
                      {t.last_dir === 'out' ? <span className="text-gray-400">{t.last_words ? `${t.last_words} · ` : 'You: '}</span> : null}{t.last_body}
                    </span>
                    {t.unread > 0 && <span className="flex-shrink-0 min-w-[1.25rem] h-5 px-1.5 rounded-full bg-brand-600 text-black text-[11px] font-black flex items-center justify-center">{t.unread}</span>}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        </div>

        {/* The open thread. */}
        <div className={`card p-0 overflow-hidden ${showList ? 'hidden lg:flex' : 'flex'} flex-col min-h-[60vh]`}>
          {!phone ? (
            <div className="flex-1 flex items-center justify-center p-8 text-sm text-gray-500">Pick a conversation, or start a new text.</div>
          ) : (
            <ConversationPane phone={phone} convo={convo} loading={loadingConvo && !convo} readOnly={readOnly}
              onBack={() => navigate('/texts')} />
          )}
        </div>
      </div>

      {picking && <NewTextPicker onClose={() => setPicking(false)} onPick={p => { setPicking(false); open(p); }} />}
    </div>
  );
}

function ConversationPane({ phone, convo, loading, readOnly, onBack }: { phone: string; convo?: Conversation; loading: boolean; readOnly: boolean; onBack: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [draft, setDraft] = useState('');
  // One picture on the next text: the shrunk file and a preview of it. Cleared with the draft.
  const [photo, setPhoto] = useState<{ file: File; preview: string } | null>(null);
  const [shrinking, setShrinking] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const count = convo?.messages.length || 0;
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [phone, count]);
  useEffect(() => { setDraft(''); setPhoto(null); }, [phone]);
  // Arriving from a Text button elsewhere (a job, the day plan, a PIN invite) with ?body=…: the words land in the box once.
  const wantedBody = searchParams.get('body');
  const bodiedFor = useRef<string | null>(null);
  useEffect(() => {
    const k = `${phone}|${wantedBody || ''}`;
    if (!wantedBody || readOnly || bodiedFor.current === k) return;
    bodiedFor.current = k;
    setDraft(wantedBody);
  }, [phone, wantedBody, readOnly]);
  useEffect(() => () => { if (photo) URL.revokeObjectURL(photo.preview); }, [photo]);
  // Is the photo disk kept across deploys? The same check as Settings → Advanced → Crew photos. When it is not,
  // the picture still goes to the customer's phone; what stops opening after the next update is the copy here.
  const { data: storage } = useQuery<{ persistent: boolean; writable: boolean }>('photo-storage',
    () => api.get('/settings/photo-storage').then(r => r.data), { staleTime: 5 * 60 * 1000, enabled: !readOnly });
  const pickPhoto = async (picked: File | undefined) => {
    if (fileRef.current) fileRef.current.value = '';
    if (!picked) return;
    if (!picked.type.startsWith('image/')) { toast.error('Pictures only (JPEG or PNG)'); return; }
    setShrinking(true);
    try {
      const file = await shrinkPhoto(picked);
      if (file.size > PHOTO_MAX_BYTES) { toast.error('That picture is too big even after shrinking — 5 MB at most'); return; }
      if (photo) URL.revokeObjectURL(photo.preview);
      setPhoto({ file, preview: URL.createObjectURL(file) });
    } finally { setShrinking(false); }
  };
  const dropPhoto = () => { if (photo) URL.revokeObjectURL(photo.preview); setPhoto(null); };

  // What this thread asked for, open ones first (server/services/textAsks). Refreshed with the thread.
  const { data: askData } = useQuery<{ asks: Ask[]; ai: boolean }>(['texts-asks', phone],
    () => api.get('/messages/asks', { params: { phone } }).then(r => r.data), { refetchInterval: 15000 });
  const asks = useMemo(() => askData?.asks || [], [askData]);
  const openAsks = asks.filter(a => a.status === 'open');
  const doneByMessage = useMemo(() => {
    const m: Record<number, Ask[]> = {};
    for (const a of asks) if (a.status === 'done') (m[a.message_id] = m[a.message_id] || []).push(a);
    return m;
  }, [asks]);
  const refreshAsks = () => { qc.invalidateQueries(['texts-asks', phone]); qc.invalidateQueries('attention'); qc.invalidateQueries('my-day-asks'); };

  // "Draft a reply": AI-written words (or the template) land in the box for him to edit. Nothing is sent by this.
  const drafting = useMutation((id: number) => api.post(`/messages/asks/${id}/draft`).then(r => r.data), {
    onSuccess: (d: any) => { if (d?.body) setDraft(d.body); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not write a draft'); },
  });
  const handled = useMutation(({ id, note }: { id: number; note: string | null }) => api.post(`/messages/asks/${id}/handled`, { note }).then(r => r.data), {
    onSuccess: (d: any) => { toast.success(d?.message || 'Marked handled'); refreshAsks(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not mark that'); },
  });
  const dismissAsk = useMutation((id: number) => api.post(`/messages/asks/${id}/dismiss`).then(r => r.data), {
    onSuccess: () => { refreshAsks(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not do that'); },
  });
  const reopen = useMutation((id: number) => api.post(`/messages/asks/${id}/reopen`).then(r => r.data), {
    onSuccess: () => { refreshAsks(); },
  });
  // Arriving from Needs Attention or My Day with ?ask=ID: the draft is written once, so the reply is one tap away.
  const wantedAsk = Number(searchParams.get('ask')) || null;
  const draftedFor = useRef<number | null>(null);
  useEffect(() => {
    if (!wantedAsk || readOnly || draftedFor.current === wantedAsk) return;
    const a = asks.find(x => x.id === wantedAsk);
    if (!a || a.status !== 'open' || draft.trim()) return;
    draftedFor.current = wantedAsk;
    drafting.mutate(wantedAsk);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wantedAsk, asks]);
  const markHandled = (a: Ask) => {
    const note = window.prompt(`How was ${a.who || 'this'} taken care of? (a call, your own phone — optional)`, '');
    if (note === null) return;
    handled.mutate({ id: a.id, note: note.trim() || null });
  };
  // Their yes by text is the signature: the open bid is accepted as their own tap would be (services/bidAccept).
  const acceptBid = useMutation((id: number) => api.post(`/messages/asks/${id}/accept-bid`).then(r => r.data), {
    onSuccess: (d: any) => {
      toast.success(d?.message || 'Accepted', { autoClose: 12000 });
      refreshAsks(); qc.invalidateQueries(['texts-thread', phone]); qc.invalidateQueries('quotes'); qc.invalidateQueries('jobs'); qc.invalidateQueries('calendar-jobs');
    },
    // 409: a gate their own tap would meet (the deposit, the card, the plan) — the words stay up long enough to read.
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not accept the bid', e?.response?.status === 409 ? { autoClose: 15000 } : undefined); },
  });
  const finish = (a: Ask) => {
    if (a.kind === 'quote') return navigate(a.customer_id ? `/calendar?quote=1&customer=${a.customer_id}` : '/calendar?quote=1');
    if (a.kind === 'reschedule') return navigate(a.customer_id ? `/customers/${a.customer_id}` : '/calendar');
    if (a.kind === 'bid_yes') {
      if (a.bid) {
        if (window.confirm(`Accept bid #${a.bid.id}${a.bid.title ? ` (${a.bid.title})` : ''} from this text? Their words are the signature. A mowing bid goes on the schedule.`)) acceptBid.mutate(a.id);
        return;
      }
      return navigate(a.customer_id ? `/customers/${a.customer_id}` : '/quotes');
    }
    drafting.mutate(a.id);
  };

  // With a picture the text goes up as a form (the api interceptor leaves the multipart type alone); words alone stay JSON.
  // `now` is the quiet-hours override ("Send now anyway"): without it a text typed after 8 PM waits for the morning.
  const send = useMutation(({ body, file, now }: { body: string; file: File | null; now?: boolean }) => {
    if (!file) return api.post(`/messages/threads/${phone}/send`, { body, ...(now ? { now: true } : {}) }).then(r => r.data);
    const form = new FormData();
    form.append('body', body);
    if (now) form.append('now', '1');
    form.append('photo', file, file.name || 'photo.jpg');
    return api.post(`/messages/threads/${phone}/send`, form).then(r => r.data);
  }, {
    onSuccess: (d: any) => {
      setDraft(''); dropPhoto();
      if (d?.held) toast.info(d.message || `Waiting until ${d.held.words}`);
      else if (d?.media_error) toast.warn(d.message || `The words went, but the photo didn't send: ${d.media_error}`);
      else if (d?.message && /Practice/.test(d.message)) toast.info(d.message);
      qc.invalidateQueries(['texts-thread', phone]);
      qc.invalidateQueries('texts-threads');
      qc.invalidateQueries('needs-attention');
    },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send that'); },
  });
  // The waiting texts' own taps: send one this minute, or drop it.
  const afterHeld = () => { qc.invalidateQueries(['texts-thread', phone]); qc.invalidateQueries('texts-threads'); qc.invalidateQueries('needs-attention'); };
  const sendNow = useMutation((id: number) => api.post(`/messages/held/${id}/send-now`).then(r => r.data), {
    onSuccess: (d: any) => { if (d?.media_error) toast.warn(d.message); else toast.success(d?.message || 'Sent'); afterHeld(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not send that'); afterHeld(); },
  });
  const cancelHeld = useMutation((id: number) => api.delete(`/messages/held/${id}`).then(r => r.data), {
    onSuccess: () => { toast.info('Dropped — it will not be sent'); afterHeld(); },
    onError: (e: any) => { toast.error(e?.response?.data?.message || 'Could not cancel that'); afterHeld(); },
  });
  const held = convo?.held || [];
  const quiet = convo?.quiet;
  const canSend = !readOnly && !send.isLoading && !shrinking && (!!draft.trim() || !!photo) && !!convo && !convo.opted_out;
  const submit = (now = false) => { if (canSend) send.mutate({ body: draft.trim(), file: photo?.file || null, now }); };
  const len = draft.length;
  // Tapping the name at the top of a thread opens who they are (owner, Nov 3 2026) — a customer's own card
  // (/customers/:id opens that card), the Leads page, the crew list. A number the app does not know has
  // nothing to open: the name stays plain text and says so, rather than a link that goes nowhere.
  const whoId = convo?.customer_id || convo?.who_id || null;
  const whoLink = convo?.kind === 'customer' && whoId ? `/customers/${whoId}`
    : convo?.kind === 'lead' ? '/leads' : convo?.kind === 'crew' ? '/employees' : null;
  const whoWords = convo?.kind === 'customer' ? `Open ${convo?.name || 'this customer'}'s profile`
    : convo?.kind === 'lead' ? 'Open the Leads page' : convo?.kind === 'crew' ? 'Open the crew list' : undefined;

  const name = (
    <h2 className={`text-base font-black text-gray-900 truncate${whoLink ? ' group-hover:underline' : ''}`}
      title={whoLink ? undefined : convo ? 'This number is not in the app yet' : undefined}>
      {convo?.name || convo?.pretty || phone}
      {convo?.label && <span className="font-semibold text-gray-500"> · {convo.label}</span>}
      <AccountMark mark={convo?.account_mark} />
    </h2>
  );

  return (
    <>
      <div className="px-3 sm:px-4 py-3 border-b border-gray-100 flex items-center gap-3">
        <button onClick={onBack} className="press lg:hidden h-9 w-9 -ml-1 flex items-center justify-center rounded-xl text-gray-700 hover:bg-gray-100" aria-label="Back to the list">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 min-w-0">
            {whoLink ? (
              <Link to={whoLink} title={whoWords} aria-label={whoWords}
                className="press group min-w-0 flex items-center gap-1 -ml-1 px-1 rounded-lg hover:bg-gray-100">
                {name}
                <ChevronRight className="h-4 w-4 flex-shrink-0 text-gray-400 group-hover:text-brand-700" />
              </Link>
            ) : name}
            {convo && KIND_WORDS[convo.kind] && <span className="text-[10px] font-bold uppercase tracking-wide text-gray-500 flex-shrink-0">{KIND_WORDS[convo.kind]}</span>}
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            {convo?.name && <span className="flex items-center gap-1"><Phone className="h-3 w-3" />{convo.pretty}</span>}
            <a href={`tel:${convo?.to || `+1${phone}`}`} className="flex items-center gap-1 hover:underline">Call</a>
          </div>
        </div>
      </div>

      {openAsks.length > 0 && (
        <div className="px-3 sm:px-4 py-2 border-b border-amber-200 bg-amber-50 space-y-2">
          {openAsks.map(a => (
            <div key={a.id} className={`rounded-xl border bg-white px-3 py-2 ${wantedAsk === a.id ? 'border-amber-500 ring-2 ring-amber-200' : 'border-amber-200'}`}>
              <p className="text-sm font-semibold text-gray-900">{a.line}</p>
              <p className="text-xs text-gray-600 mt-0.5">{a.summary}</p>
              {!readOnly && (
                <div className="mt-2 flex flex-wrap items-center gap-1.5">
                  {a.kind !== 'question' && a.kind !== 'other' && a.kind !== 'complaint' && a.kind !== 'cancel' && a.kind !== 'pause' && (
                    <button onClick={() => finish(a)} disabled={acceptBid.isLoading} className="press px-2.5 py-1.5 rounded-lg bg-brand-600 text-black text-xs font-bold flex items-center gap-1 disabled:opacity-50">
                      {a.kind === 'bid_yes' && a.bid ? <Check className="h-3.5 w-3.5" /> : <CalendarPlus className="h-3.5 w-3.5" />} {acceptBid.isLoading && a.kind === 'bid_yes' ? 'Accepting…' : a.action}
                    </button>
                  )}
                  <button onClick={() => drafting.mutate(a.id)} disabled={drafting.isLoading}
                    className={`press px-2.5 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1 disabled:opacity-50 ${['question', 'other', 'complaint', 'cancel', 'pause'].includes(a.kind) ? 'bg-brand-600 text-black' : 'border border-gray-300 text-gray-800 bg-white'}`}>
                    <Sparkles className="h-3.5 w-3.5" /> {drafting.isLoading ? 'Writing…' : 'Draft a reply'}
                  </button>
                  <button onClick={() => markHandled(a)} disabled={handled.isLoading}
                    className="press px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-gray-800 text-xs font-bold flex items-center gap-1 disabled:opacity-50">
                    <Check className="h-3.5 w-3.5" /> Handled outside the app
                  </button>
                  <button onClick={() => dismissAsk.mutate(a.id)} className="ml-auto text-[11px] text-gray-500 underline">Not an ask</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
      <div className="flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-2 max-h-[60vh] lg:max-h-[62vh] bg-gray-50/60">
        {loading && <p className="text-sm text-gray-500">Loading…</p>}
        {convo && !convo.messages.length && !held.length && (
          <p className="text-sm text-gray-500 text-center py-6">No texts with {convo.name || convo.pretty} yet. Whatever you type below goes from the business number.</p>
        )}
        {convo?.messages.map((m, i) => {
          const prev = convo.messages[i - 1];
          const newDay = !prev || dayOf(prev.at) !== dayOf(m.at);
          return (
            <React.Fragment key={m.id}>
              {newDay && <div className="text-center text-[11px] font-semibold text-gray-400 py-1">{dayLabel(m.at)}</div>}
              <Bubble m={m} />
              {(doneByMessage[m.id] || []).map(a => (
                <div key={a.id} className="flex justify-start -mt-1">
                  <button onClick={() => !readOnly && reopen.mutate(a.id)} title="Finished — tap to put it back on the list"
                    className="text-[11px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5 flex items-center gap-1">
                    <Check className="h-3 w-3" /> {a.line}
                  </button>
                </div>
              ))}
            </React.Fragment>
          );
        })}
        {held.map(h => (
          <HeldBubble key={`held-${h.id}`} h={h} readOnly={readOnly} busy={sendNow.isLoading || cancelHeld.isLoading}
            onSend={() => sendNow.mutate(h.id)}
            onCancel={() => { if (window.confirm('Drop this text? It will not be sent.')) cancelHeld.mutate(h.id); }} />
        ))}
        <div ref={endRef} />
      </div>

      <div className="border-t border-gray-100 p-3">
        {convo?.opted_out ? (
          <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
            {convo.name || 'They'} replied STOP, so the app will not text them. Call them instead.
          </p>
        ) : readOnly ? (
          <p className="text-sm text-gray-500">View only — this login cannot send texts.</p>
        ) : (
          <>
            {photo && (
              <div className="mb-2 flex items-center gap-3">
                <a href={photo.preview} target="_blank" rel="noreferrer" className="block flex-shrink-0">
                  <img src={photo.preview} alt="Going on this text" className="h-20 w-20 object-cover rounded-xl border border-gray-200" />
                </a>
                <div className="min-w-0 flex-1 text-xs text-gray-600">
                  <p className="font-semibold text-gray-800">Goes with this text</p>
                  <p>{Math.max(1, Math.round(photo.file.size / 1024))} KB · one picture per text</p>
                </div>
                <button onClick={dropPhoto} className="press h-9 w-9 flex items-center justify-center rounded-xl text-gray-600 hover:bg-gray-100" aria-label="Take the picture off" title="Take the picture off">
                  <X className="h-5 w-5" />
                </button>
              </div>
            )}
            <div className="flex items-end gap-2">
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={e => pickPhoto(e.target.files?.[0])} />
              <button onClick={() => fileRef.current?.click()} disabled={shrinking || send.isLoading}
                className={`press h-11 w-11 flex-shrink-0 flex items-center justify-center rounded-xl border ${photo ? 'border-brand-500 bg-brand-50 text-brand-800' : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'} disabled:opacity-50`}
                aria-label={photo ? 'Swap the picture' : 'Add a photo'} title={photo ? 'Swap the picture' : 'Add a photo'}>
                <ImagePlus className="h-5 w-5" />
              </button>
              <textarea value={draft} onChange={e => setDraft(e.target.value)}
                onKeyDown={e => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') { e.preventDefault(); submit(); } }}
                rows={Math.min(5, Math.max(1, draft.split('\n').length))}
                placeholder={photo ? 'Say something with the picture (optional)…' : `Text ${convo?.name || convo?.pretty || 'them'}…`}
                className="input flex-1 resize-none py-2.5" />
              <button onClick={() => submit()} disabled={!canSend} className="btn-primary h-11 px-4"
                title={quiet?.active ? `Waits until ${quiet.words} — quiet hours (Ctrl+Enter)` : 'Send (Ctrl+Enter)'}>
                {quiet?.active ? <Clock className="h-4 w-4" /> : <Send className="h-4 w-4" />}
              </button>
            </div>
            {quiet?.active && (
              <p className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1 flex items-center gap-2">
                <Clock className="h-3 w-3 flex-shrink-0" />
                <span>It's after {quiet.from_words} — this text waits until {quiet.words}.</span>
                <button onClick={() => submit(true)} disabled={!canSend} className="ml-auto font-bold underline whitespace-nowrap disabled:opacity-50">Send now anyway</button>
              </p>
            )}
            {shrinking && <p className="mt-1 text-[11px] text-gray-500">Shrinking the picture…</p>}
            {storage && (!storage.persistent || !storage.writable) && (
              <p className="mt-1.5 text-[11px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2 py-1">
                {!storage.writable
                  ? 'Pictures cannot be saved on this server right now, so a photo will not go on a text. The words still go.'
                  : 'Photos are on temporary storage: the customer gets the picture, but the copy here stops opening after the next app update (Settings → Advanced → Crew photos says how to fix it).'}
              </p>
            )}
          </>
        )}
        {len > 160 && <p className="mt-1 text-[11px] text-gray-500">{len} characters · goes as {Math.ceil(len / 153)} texts</p>}
      </div>
    </>
  );
}

function Bubble({ m }: { m: Msg }) {
  const out = m.dir === 'out';
  const link = m.invoice_id ? { to: `/billing?invoice=${m.invoice_id}`, words: 'Open bill' }
    : m.quote_id ? { to: `/quotes?open=${m.quote_id}`, words: 'Open bid' }
      : m.job_id ? { to: `/jobs/${m.job_id}`, words: 'Open job' }
        : m.request_id ? { to: `/attention#service:${m.request_id}`, words: 'Open on Needs Attention' } : null;
  // A request typed on their customer page reads as theirs (left, incoming) with a dashed edge: not a text, but
  // the reply typed below still goes to them by text.
  const theirs = m.portal ? 'bg-white text-gray-900 border border-dashed border-brand-400' : 'bg-white text-gray-900 border border-gray-100';
  return (
    <div className={`flex ${out ? 'justify-end' : 'justify-start'}`}>
      <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3.5 py-2 shadow-soft ${out ? (m.typed ? 'bg-brand-600 text-black' : 'bg-brand-100 text-gray-900') : theirs} ${m.failed ? 'ring-2 ring-red-300' : ''}`}>
        {/* Theirs is proxied under /api (messages/media); ours is the signed /uploads file at the server's root. */}
        {m.media.map((p, i) => <Picture key={i} url={`${p.ours ? SERVER_ORIGIN : API_BASE_URL}${p.url}`} image={/^image\//.test(p.type || '')} ours={!!p.ours} dim={!!m.media_failed} />)}
        {m.body && <p className="text-sm whitespace-pre-wrap break-words">{m.body}</p>}
        <div className={`mt-1 flex items-center gap-2 text-[10px] ${out && m.typed ? 'text-black/70' : 'text-gray-500'}`}>
          <span>{fmtTs(m.at, { hour: 'numeric', minute: '2-digit' })}</span>
          {m.words && <span className="font-bold uppercase tracking-wide">{m.words}</span>}
          {out && m.by && m.by !== 'the app' && <span>{m.by}</span>}
          {link && <Link to={link.to} className="font-semibold underline">{link.words}</Link>}
          {m.failed && <span className="font-bold text-red-700">Not delivered{m.error ? ` — ${m.error}` : ''}</span>}
          {!m.failed && m.media_failed && <span className="font-bold text-red-700">Photo didn't send — {m.media_failed}</span>}
        </div>
      </div>
    </div>
  );
}

/**
 * A text typed in the quiet hours, waiting on the thread for the morning (owner, Oct 30 2026: "If I send any text
 * past 8 p.m., wait until 6 a.m. to send it unless overwritten"). Dashed, amber, on our side; "Send now" is the
 * override, Cancel drops it. One the morning refused reads red with why, and "Try again".
 */
function HeldBubble({ h, readOnly, busy, onSend, onCancel }: { h: Held; readOnly: boolean; busy: boolean; onSend: () => void; onCancel: () => void }) {
  const link = h.quote_id ? { to: `/quotes?open=${h.quote_id}`, words: 'Open bid' } : h.job_id ? { to: `/jobs/${h.job_id}`, words: 'Open job' } : null;
  return (
    <div className="flex justify-end">
      <div className={`max-w-[85%] sm:max-w-[70%] rounded-2xl px-3.5 py-2 border-2 border-dashed text-gray-900 ${h.failed ? 'border-red-300 bg-red-50' : 'border-amber-400 bg-amber-50'}`}>
        {h.media && <Picture url={`${SERVER_ORIGIN}${h.media.url}`} image ours />}
        {h.body && <p className="text-sm whitespace-pre-wrap break-words">{h.body}</p>}
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[10px] text-gray-600">
          <span className="font-bold uppercase tracking-wide flex items-center gap-1"><Clock className="h-3 w-3" /> {h.failed ? "Didn't send" : `Waiting until ${h.words}`}</span>
          {h.by && <span>{h.by}</span>}
          {link && <Link to={link.to} className="font-semibold underline">{link.words}</Link>}
          {h.failed && h.error && <span className="font-bold text-red-700 normal-case tracking-normal">{h.error}</span>}
        </div>
        {!readOnly && (
          <div className="mt-1.5 flex items-center gap-2">
            <button onClick={onSend} disabled={busy} className="press px-2.5 py-1 rounded-lg bg-brand-600 text-black text-xs font-bold disabled:opacity-50">{h.failed ? 'Try again' : 'Send now'}</button>
            <button onClick={onCancel} disabled={busy} className="press px-2.5 py-1 rounded-lg border border-gray-300 bg-white text-gray-800 text-xs font-bold disabled:opacity-50">Cancel</button>
          </div>
        )}
      </div>
    </div>
  );
}

/**
 * A picture on a text: a thumbnail that opens full size in a new tab. Theirs is fetched from Twilio (kept for a
 * while, not forever); ours is the file on the photo disk (gone after a deploy without a volume). Either way one
 * that can no longer be fetched says so instead of a broken frame. `dim` = the office's picture Twilio refused.
 */
function Picture({ url, image, ours = false, dim = false }: { url: string; image: boolean; ours?: boolean; dim?: boolean }) {
  const [gone, setGone] = useState(false);
  if (!image) return <a href={url} target="_blank" rel="noreferrer" className="block mb-1 text-xs underline">Attachment</a>;
  if (gone) return <p className="mb-1 text-[11px] italic opacity-70">{ours ? 'Picture is no longer on the server' : 'Picture no longer available'}</p>;
  return (
    <a href={url} target="_blank" rel="noreferrer" className="block mb-1" title="Open full size">
      <img src={url} alt="" loading="lazy" onError={() => setGone(true)} className={`max-h-48 max-w-full rounded-xl ${dim ? 'opacity-50' : ''}`} />
    </a>
  );
}

/** Start a text: a customer by name or number, or a number typed in. */
function NewTextPicker({ onClose, onPick }: { onClose: () => void; onPick: (phone: string) => void }) {
  const [q, setQ] = useState('');
  const { data } = useQuery(['texts-pick', q], () => api.get('/customers', { params: { search: q } }).then(r => r.data), { enabled: q.trim().length >= 2, keepPreviousData: true });
  const customers: any[] = Array.isArray(data) ? data : (data?.customers || []);
  const typed = digits10(q);
  const rows = customers.filter(c => c.phone || c.mobile_phone).slice(0, 12);
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4" onClick={onClose}>
      <div className="w-full sm:max-w-md bg-white rounded-t-3xl sm:rounded-3xl shadow-lift p-4 space-y-3" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <h3 className="text-base font-black uppercase tracking-tight text-gray-900 flex items-center gap-2"><MessageSquare className="h-4 w-4" /> New text</h3>
          <button onClick={onClose} className="press h-9 w-9 flex items-center justify-center rounded-xl hover:bg-gray-100" aria-label="Close"><X className="h-5 w-5" /></button>
        </div>
        <input autoFocus value={q} onChange={e => setQ(e.target.value)} placeholder="Customer name, or a phone number" className="input w-full" />
        <ul className="divide-y divide-gray-100 max-h-72 overflow-y-auto">
          {typed.length === 10 && q.replace(/\D/g, '').length <= 11 && (
            <li><button onClick={() => onPick(typed)} className="w-full text-left px-2 py-2 hover:bg-gray-50 text-sm font-semibold">Text ({typed.slice(0, 3)}) {typed.slice(3, 6)}-{typed.slice(6)}</button></li>
          )}
          {rows.map(c => {
            const p = digits10(String(c.mobile_phone || c.phone || ''));
            return (
              <li key={c.id}>
                <button onClick={() => onPick(p)} disabled={p.length !== 10} className="w-full text-left px-2 py-2 hover:bg-gray-50 disabled:opacity-50">
                  <span className="block text-sm font-semibold text-gray-900">{c.display_name || c.name}<AccountMark mark={c.account_mark} /></span>
                  <span className="block text-xs text-gray-500">{c.mobile_phone || c.phone}{c.sms_opt_out ? ' · replied STOP' : ''}</span>
                </button>
              </li>
            );
          })}
          {q.trim().length >= 2 && !rows.length && typed.length !== 10 && <li className="px-2 py-3 text-sm text-gray-500">Nobody by that name with a phone on file.</li>}
        </ul>
      </div>
    </div>
  );
}
